import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Generate or get visitor ID (persists across sessions)
const getVisitorId = () => {
  let visitorId = localStorage.getItem('visitor_id');
  if (!visitorId) {
    visitorId = 'v_' + Math.random().toString(36).substr(2, 12) + '_' + Date.now();
    localStorage.setItem('visitor_id', visitorId);
  }
  return visitorId;
};

// Get session ID (new per browser session)
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('session_id');
  if (!sessionId) {
    sessionId = 's_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
    sessionStorage.setItem('session_id', sessionId);
  }
  return sessionId;
};

// Track event - fire and forget, non-blocking
export const trackEvent = (eventType, page = window.location.pathname, extraData = {}) => {
  const payload = {
    page,
    event_type: eventType,
    visitor_id: getVisitorId(),
    session_id: getSessionId(),
    referrer: document.referrer || '',
    screen_width: window.innerWidth,
    screen_height: window.innerHeight,
    user_language: navigator.language,
    timestamp_client: new Date().toISOString(),
    ...extraData,
  };

  // Use requestIdleCallback or setTimeout to ensure non-blocking
  const sendTracking = () => {
    axios.post(`${BACKEND_URL}/api/track`, payload, {
      timeout: 3000,
    }).catch(() => {
      // Silent fail - tracking should never block UI
    });
  };

  if ('requestIdleCallback' in window) {
    requestIdleCallback(sendTracking, { timeout: 2000 });
  } else {
    setTimeout(sendTracking, 0);
  }
};

// Hook for automatic page view tracking - optimized for performance
export const usePageTracking = () => {
  const location = useLocation();
  const lastTrackedPath = useRef('');

  useEffect(() => {
    // Skip tracking for admin page
    if (location.pathname === '/admin') {
      return;
    }

    // Only track if path actually changed
    if (location.pathname !== lastTrackedPath.current) {
      lastTrackedPath.current = location.pathname;
      // trackEvent is now non-blocking, no setTimeout needed
      trackEvent('page_view', location.pathname);
    }
  }, [location.pathname]);
};

export default usePageTracking;
